import React, { useEffect, useState } from 'react';
import { CheckCircle2, Hourglass, Mail, XCircle } from 'lucide-react';
import { Header } from '../../organisms';

type PaymentStatus = 'success' | 'failure' | 'checking' | 'pending';

interface DisplayContent {
  status: PaymentStatus;
  borderColorClass: string;
  icon: React.ReactNode;
  heading: string;
  message: string | React.ReactNode;
}

export const PaymentStatusPage: React.FC = () => {
  const [content, setContent] = useState<DisplayContent>({
    status: 'checking',
    borderColorClass: 'border-primary',
    icon: <Hourglass size={48} className='text-primary' />,
    heading: 'Status wird geprüft...',
    message: 'Bitte warten Sie, während wir Ihre Zahlung überprüfen.',
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status')?.toLowerCase();
    const statusText = urlParams.get('status_text');
    const paymentType = urlParams.get('payment_type')?.toLowerCase();

    if (paymentType === 'invoice' || paymentType === 'prepayment') {
      setContent({
        status: 'pending',
        borderColorClass: 'border-primary',
        icon: <Mail size={48} className='text-primary' />,
        heading: 'Fast geschafft! Ein letzter Schritt',
        message: (
          <>
            <p>Wir haben Ihnen eine E-Mail mit den Bankdaten für Ihre Überweisung gesendet.</p>
            <p className='mt-2'>Bitte schließen Sie nun die Zahlung ab.</p>
          </>
        ),
      });
    } else if (window.location.search === '') {
      setContent({
        status: 'success',
        borderColorClass: 'border-primary',
        icon: <CheckCircle2 size={48} className='text-primary' />,
        heading: 'Zahlung erfolgreich!',
        message: (
          <>
            <p>Vielen Dank für Ihre Zahlung. Ihre Bestellung wird bearbeitet.</p>
            <p className='mt-2 text-sm text-gray-500'>
              Bitte warten Sie einige Minuten, damit das Dashboard und Ihre Online-Zahlungen aktualisiert werden können.
            </p>
          </>
        ),
      });
    } else if (status === 'success') {
      setContent({
        status: 'success',
        borderColorClass: 'border-primary',
        icon: <CheckCircle2 size={48} className='text-primary' />,
        heading: statusText || 'Zahlung erfolgreich!',
        message: (
          <>
            <p>Vielen Dank für Ihre Zahlung. Ihre Bestellung wird bearbeitet.</p>
            <p className='mt-2 text-sm text-gray-500'>
              Bitte warten Sie einige Minuten, damit das Dashboard und Ihre Online-Zahlungen aktualisiert werden können.
            </p>
          </>
        ),
      });
    } else {
      setContent({
        status: 'failure',
        borderColorClass: 'border-primary',
        icon: <XCircle size={48} className='text-primary' />,
        heading: statusText || 'Zahlung fehlgeschlagen',
        message: (
          <>
            Ihre Zahlung konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut oder{' '}
            <a href='/contact' className='underline text-blue-600 hover:text-blue-800'>
              kontaktieren Sie den Support
            </a>
            .
          </>
        ),
      });
    }
  }, []);

  return (
    <>
      <Header />
      <div className='flex items-center justify-center mt-10'>
        <div
          className={`bg-white p-10 rounded-lg shadow-lg text-center w-full max-w-md border-t-4 ${content.borderColorClass}`}
        >
          <div className='flex justify-center mb-5'>{content.icon}</div>

          <h1 className='text-2xl font-bold text-gray-800 mb-2'>{content.heading}</h1>

          <div className='text-gray-600'>{content.message}</div>
        </div>
      </div>
    </>
  );
};
